import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react'
import { useHall } from './HallProvider'
import { Turn, TurnStatus } from '@db8/shared'
import { useRoomAudioRecorder } from '@/hooks/use-room-audio-recorder'

export type TurnsContextType = {
  timer: number
  resetTimer: (minutes: number, isLocal?: boolean) => void
  turnsComplete: () => boolean
  pinnedParticipants: string[]
  currentTurn: Turn
  setTurnUser: (turnUserData: string, position: 'primary' | 'secondary') => void
  maxMembers: number
  setMaxMembers: Dispatch<SetStateAction<number>>
  message: string
  setMessage: Dispatch<SetStateAction<string>>
  status: TurnStatus
  error: string
  setError: Dispatch<SetStateAction<string>>
  startRecording: () => void
  stopRecording: () => void
  audioFile: any
  setAudioFile: Dispatch<SetStateAction<File>>
}

export const TurnsContext = createContext<TurnsContextType | undefined>(undefined)

export const TurnsProvider = ({ children }: { children: React.ReactNode }) => {
  const { setHallMetadata, hallMetadata } = useHall()
  const [maxMembers, setMaxMembers] = useState(hallMetadata?.maxMembers)
  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string>('')
  const { startRecording, stopRecording, file, setFile } = useRoomAudioRecorder()
  const [timer, setTimer] = useState(0)

  const currentTurn = useMemo(
    () => hallMetadata?.turns[hallMetadata?.activeTurn],
    [hallMetadata?.activeTurn, hallMetadata?.turns],
  )
  const turns = useMemo(() => hallMetadata?.turns, [hallMetadata])

  useEffect(() => {
    setTimer(hallMetadata.timer)
  }, [hallMetadata.timer])

  useEffect(() => {
    const nextTurn = turns[hallMetadata?.activeTurn]
    if (!nextTurn) return
  }, [hallMetadata?.activeTurn])

  useEffect(() => {
    if ([TurnStatus.PLAYING, TurnStatus.RECORDING].includes(hallMetadata?.status) && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => Math.max(0, prev - 1000))
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [hallMetadata?.status, timer])

  const resetTimer = (minutes: number) => {
    let newTime
    if (minutes) newTime = minutes * 60 * 1000
    else newTime = currentTurn?.duration * 60 * 1000

    setTimer(newTime)
  }

  const setTurnUser = (turnUserData: string, position: 'primary' | 'secondary' = 'primary') => {
    const { userId, step } = JSON.parse(turnUserData) as {
      userId: string
      step: number
    }

    const updatedTurns = turns.map((turn) => {
      if (turn.step !== step) return turn

      const updated = { ...turn }
      if (position === 'primary') {
        updated.primarySpeakerId = userId
      } else {
        updated.secondarySpeakerId = userId
      }
      return updated
    })

    setHallMetadata({ turns: updatedTurns })
  }

  const turnsComplete = () => hallMetadata?.activeTurn > turns?.at(-1)?.step!

  return (
    <TurnsContext.Provider
      value={{
        timer,
        resetTimer,
        turnsComplete,
        pinnedParticipants: hallMetadata.pinnedParticipants,
        currentTurn,
        setTurnUser,
        maxMembers,
        setMaxMembers,
        setMessage,
        message,
        status: hallMetadata?.status,
        setError,
        error,
        startRecording,
        stopRecording,
        audioFile: file,
        setAudioFile: setFile,
      }}
    >
      {children}
    </TurnsContext.Provider>
  )
}

export const useTurns = () => {
  const context = useContext(TurnsContext)
  if (!context) {
    throw new Error('useTurns must be used within a TurnsProvider')
  }
  return context
}
